import { useEffect } from 'react'
import { isRouteErrorResponse, useRouteError } from 'react-router-dom'
import { IdeyaButton } from '../common/antd/ideya-button/ideya-button.tsx'
import IdeyaTypography from '../common/antd/ideya-typography.tsx'
import { newRelic } from '../utils/new-relic.ts'

// React Router catches render/loader errors before they reach window.onerror,
// so the New Relic agent's automatic instrumentation never sees them. Report
// them explicitly. Module-level WeakSet (not useRef) because production renders
// without StrictMode while the internal site renders with it, so effects run a
// different number of times per path.
const reportedErrors = new WeakSet<Error>()

export const RouteError = () => {
  const error = useRouteError()
  // Unmatched URLs surface here as a router 404 (no catch-all route exists).
  // Reloading a bad URL just re-renders this page, so send the user home instead.
  const isNotFound = isRouteErrorResponse(error) || error instanceof Response

  useEffect(() => {
    // Thrown Responses (404s, redirects) are routing control flow, not faults.
    if (isNotFound) return
    if (!(error instanceof Error) || reportedErrors.has(error)) return

    reportedErrors.add(error)
    newRelic.noticeError(error, { context: 'routeError' })
  }, [error, isNotFound])

  return (
    <div
      role={'alert'}
      style={{
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
        gap: '32px',
      }}
    >
      <IdeyaTypography.Title level={3}>
        {isNotFound
          ? 'Page not found.'
          : 'Something went wrong. Please reload the page.'}
      </IdeyaTypography.Title>
      <div>
        <IdeyaButton
          type={'primary'}
          size={'large'}
          onClick={() =>
            isNotFound
              ? window.location.replace('/idea')
              : window.location.reload()
          }
        >
          Reload
        </IdeyaButton>
      </div>
    </div>
  )
}
